"use client";

import {
  isOnboardingFreeResumePath,
  isOnboardingTrialResumePath,
} from "@/components/onboarding/quiz/experience";
import { useRouter } from "@/hooks/useSchoolRouter";
import { isGenuineEmailSignup } from "@/lib/analytics/genuine-email-signup";
import { trackEmailSignupConversion } from "@/lib/analytics/google-ads-tracking";
import { stageOAuthPostHogBootstrap } from "@/lib/analytics/oauth-posthog-bootstrap";
import { getAttributionData } from "@/lib/analytics/utm-tracker";
import { getGuestSpatiusAvatarExposureProperties } from "@/lib/experiments/guestSpatiusAvatar";
import { stageGuestVoiceSignup } from "@/lib/experiments/guestVoiceRuntimeClient";
import {
  getWebappSignupBeforeDemoAttributionProperties,
  isWebappSignupBeforeDemoReturnPath,
} from "@/lib/experiments/webappSignupBeforeDemo";
import { createLogger } from "@/lib/logger";
import { isPWA } from "@/lib/pwa-utils";
import { isAnonymousPurchaseActivationPath } from "@/lib/revenuecat/redemption";
import { isSchoolPath, schoolAuthReturnTo, schoolHref } from "@/lib/school-routes";
import { supabase } from "@/lib/supabaseClient";
import { registerTutorAccount } from "@/lib/tutor-studio/registerTutorAccount";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Mail } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { FormEvent, useEffect, useState } from "react";

interface AuthFormProps {
  onSuccess?: () => void;
  redirectPath?: string;
  defaultMode?: "signup" | "signin";
  intent?: "tutor";
  headingLevel?: 1 | 2;
  variant?: "modal" | "page";
  titleId?: string;
}

const logger = createLogger("AuthForm");

const inputClassName =
  "block h-12 w-full rounded-xl border border-white/10 bg-white/[0.04] text-sm font-semibold text-white placeholder:text-white/35 outline-none transition-colors hover:bg-white/[0.06] focus:border-orange-300/55";
const secondaryButtonClassName =
  "flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] text-sm font-semibold text-white transition-colors hover:border-orange-300/35 hover:bg-white/[0.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60 disabled:cursor-not-allowed disabled:opacity-60";
const primaryButtonClassName =
  "flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-orange-300/70 bg-orange-500 text-sm font-bold text-white transition-colors hover:bg-orange-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60 disabled:cursor-not-allowed disabled:opacity-70";
const labelClassName = "block text-sm font-semibold text-white/60";

function GoogleLogo() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.797 2.716v2.258h2.909c1.702-1.567 2.684-3.874 2.684-6.614Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.468-.806 5.956-2.181l-2.909-2.258c-.806.54-1.835.859-3.047.859-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.963 10.706A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.168.281-1.706V4.962H.956A9 9 0 0 0 0 9c0 1.45.347 2.824.956 4.038l3.007-2.332Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.507.454 3.441 1.346l2.581-2.582C13.464.892 11.428 0 9 0A9 9 0 0 0 .956 4.962l3.007 2.332C4.672 5.165 6.656 3.58 9 3.58Z"
      />
    </svg>
  );
}

export function AuthForm({
  onSuccess,
  redirectPath,
  defaultMode = "signup",
  intent,
  headingLevel = 1,
  variant = "page",
  titleId,
}: AuthFormProps) {
  const [authMode, setAuthMode] = useState<"signup" | "signin">(defaultMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRunningAsPWA, setIsRunningAsPWA] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const resolvedRedirect = schoolAuthReturnTo(
    pathname || "/",
    searchParams?.toString() || "",
    redirectPath || searchParams?.get("redirect")
  );
  const posthog = usePostHog();
  const authIntent = intent || searchParams?.get("intent");
  const isAffiliateIntent = authIntent === "affiliate";
  const isGuestTrialIntent = authIntent === "trial";
  const isGuestFreeContinuationIntent = authIntent === "free_continue";
  const isFreeDemoIntent = authIntent === "free_demo";
  const isOnboardingFreeIntent = authIntent === "onboarding_free";
  const isTutorIntent = authIntent === "tutor";

  const authTitle =
    authMode === "signin"
      ? isAffiliateIntent
        ? "Sign in to your affiliate workspace"
        : isTutorIntent
          ? "Sign in to open Tutor Studio"
          : isFreeDemoIntent
            ? "Sign in to start speaking"
            : isGuestTrialIntent
              ? "Sign in to start your trial"
              : isOnboardingFreeIntent
                ? "Sign in to continue free"
                : isGuestFreeContinuationIntent
                  ? "Sign in to continue"
                  : "Welcome back"
      : isAffiliateIntent
        ? "Create your affiliate account"
        : isTutorIntent
          ? "Create your tutor account"
          : isFreeDemoIntent
            ? "Create your free account to start speaking"
            : isGuestTrialIntent
              ? "Create your account to start your trial"
              : isOnboardingFreeIntent
                ? "Create your free account to keep speaking"
                : isGuestFreeContinuationIntent
                  ? "Create your free account to continue"
                  : "Get started";

  const authDescription =
    authMode === "signin"
      ? isAffiliateIntent
        ? "Access your tracked link, onboarding checklist, and promotion resources."
        : isTutorIntent
          ? "Your product tour is complete. Sign in to open your curriculum and approved student progress in Tutor Studio."
          : isFreeDemoIntent
            ? "Your practice choices are ready. Sign in to begin your free speaking demo."
            : isGuestTrialIntent
              ? "Your plans and renewal terms will be shown before checkout."
              : isOnboardingFreeIntent
                ? "Your free account includes 10 minutes of speaking practice each day. No card required."
                : isGuestFreeContinuationIntent
                  ? "Continue with the free practice remaining on your account."
                  : "Sign in to continue your progress."
      : isAffiliateIntent
        ? "Get your short code, tracked link, and approved promotion resources."
        : isTutorIntent
          ? "Your account opens in Tutor Studio, where you can choose practice and monitor progress after students approve access."
          : isFreeDemoIntent
            ? "Create a free account, then start your speaking demo with the choices you already made."
            : isGuestTrialIntent
              ? "Next, choose your plan and review the trial terms before checkout."
              : isOnboardingFreeIntent
                ? "Your free account includes 10 minutes of speaking practice each day. No card required."
                : isGuestFreeContinuationIntent
                  ? "Sign up free and continue for 5 more minutes today."
                  : "Create your free account to keep learning.";

  // Detect PWA mode
  useEffect(() => {
    setIsRunningAsPWA(isPWA());
  }, []);

  // Update authMode when defaultMode changes
  useEffect(() => {
    setAuthMode(defaultMode);
  }, [defaultMode]);

  // Store redirect destination
  useEffect(() => {
    const redirectParam = searchParams?.get("redirect");
    if (redirectParam) {
      localStorage.setItem("auth_redirect", resolvedRedirect || redirectParam);
      logger.info("Stored auth redirect target", { redirect: redirectParam });
    }
  }, [resolvedRedirect, searchParams]);

  const handleAuthSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    if (authMode === "signup") {
      if (password !== confirmPassword) {
        setMessage("Passwords do not match.");
        setIsLoading(false);
        return;
      }
      const redirectTarget = resolvedRedirect;
      const onboardingDemoRedirect = redirectTarget?.startsWith("/onboarding?resume=demo")
        ? redirectTarget
        : null;
      const webappDemoRedirect = isWebappSignupBeforeDemoReturnPath(redirectTarget)
        ? redirectTarget
        : null;
      const onboardingTrialRedirect = isOnboardingTrialResumePath(redirectTarget)
        ? redirectTarget
        : null;
      const onboardingFreeRedirect = isOnboardingFreeResumePath(redirectTarget)
        ? redirectTarget
        : null;
      const anonymousPurchaseActivationRedirect = isAnonymousPurchaseActivationPath(redirectTarget)
        ? redirectTarget
        : null;
      const callbackRedirect =
        onboardingDemoRedirect ||
        onboardingTrialRedirect ||
        onboardingFreeRedirect ||
        anonymousPurchaseActivationRedirect ||
        webappDemoRedirect ||
        (isTutorIntent || isSchoolPath(redirectTarget) ? redirectTarget : null);
      const callbackParams = callbackRedirect
        ? new URLSearchParams({
            redirect: callbackRedirect,
            ...(isTutorIntent ? { intent: "tutor" } : {}),
          })
        : null;
      const emailRedirectTo =
        callbackParams && typeof window !== "undefined"
          ? `${window.location.origin}/auth/callback?${callbackParams.toString()}`
          : undefined;
      await stageGuestVoiceSignup();
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        ...(emailRedirectTo ? { options: { emailRedirectTo } } : {}),
      });
      if (error) {
        setMessage(error.message);
        posthog?.capture("signup_error", {
          method: "email",
          error: error.message,
          ...getWebappSignupBeforeDemoAttributionProperties(),
        });
      } else {
        if (data?.session && isTutorIntent) {
          try {
            await registerTutorAccount(data.session.access_token);
          } catch (registrationError) {
            logger.error("Tutor profile registration failed after email signup", {
              error: registrationError,
            });
            setMessage(
              "Your account was created, but Tutor Studio setup failed. Please sign in to retry."
            );
            setIsLoading(false);
            return;
          }
        }

        if (isGenuineEmailSignup(data?.user)) {
          posthog?.capture("user_signed_up", {
            method: "email",
            $set: { email: email },
            ...getGuestSpatiusAvatarExposureProperties(),
            ...getWebappSignupBeforeDemoAttributionProperties(),
          });

          trackEmailSignupConversion({
            value: 0.1,
            currency: "USD",
          });
        }

        if (data?.session) {
          const redirectTo = resolvedRedirect || "/";
          logger.info("Email sign-up completed", {
            authMode,
            redirectTo,
            hasSession: true,
          });
          if (onSuccess) {
            onSuccess();
          } else {
            router.push(redirectTo);
          }
        } else {
          logger.info("Email sign-up requires verification before redirect", {
            authMode,
            email,
          });
          setMessage("Please check your email to verify your account.");
        }
      }
    } else {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setMessage(error.message);
        posthog?.capture("signin_error", {
          method: "email",
          error: error.message,
        });
      } else {
        if (isTutorIntent) {
          try {
            await registerTutorAccount(data.session?.access_token);
          } catch (registrationError) {
            logger.error("Tutor profile registration failed after sign in", {
              error: registrationError,
            });
            setMessage("We could not finish setting up Tutor Studio. Please try again.");
            setIsLoading(false);
            return;
          }
        }

        posthog?.capture("user_signed_in", {
          method: "email",
          ...getWebappSignupBeforeDemoAttributionProperties(),
        });

        const redirectTo = resolvedRedirect || "/";
        logger.info("Email sign-in completed", {
          authMode,
          redirectTo,
        });

        if (onSuccess) {
          onSuccess();
        } else {
          router.push(redirectTo);
        }
      }
    }
    setIsLoading(false);
  };

  const toggleAuthMode = () => {
    setAuthMode((prevMode) => (prevMode === "signup" ? "signin" : "signup"));
    setMessage("");
    setPassword("");
    setConfirmPassword("");
  };

  const handleForgotPassword = async () => {
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      setMessage("Enter your email address first, then choose Forgot?");
      return;
    }

    setIsLoading(true);
    setMessage("");

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const resetPath = schoolHref("/auth/reset-password", resolvedRedirect);
    const resetQuery = isSchoolPath(resolvedRedirect)
      ? `?${new URLSearchParams({ redirect: resolvedRedirect! }).toString()}`
      : "";
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: `${origin}${resetPath}${resetQuery}`,
      });
      if (error) {
        setMessage(
          error.status === 429
            ? "Please wait a minute before requesting another reset link."
            : "We couldn't request a reset link. Check your connection and try again."
        );
        posthog?.capture("password_reset_error", { error: error.code || "request_failed" });
      } else {
        setMessage(
          "If an account exists for this email, you'll receive a reset link. Check your inbox and spam folder, and open the most recent email."
        );
        posthog?.capture("password_reset_requested");
      }
    } catch {
      setMessage("We couldn't request a reset link. Check your connection and try again.");
      posthog?.capture("password_reset_error", { error: "request_failed" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setMessage("");

    const sessionId = posthog?.get_session_id?.();
    const distinctId = posthog?.get_distinct_id?.();
    const attributionData = getAttributionData();
    const redirectParam = resolvedRedirect;

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    let redirectTo = `${origin}/auth/callback`;

    const params = new URLSearchParams();

    if (isRunningAsPWA) params.set("pwa", "true");
    if (redirectParam) params.set("redirect", redirectParam);
    if (isTutorIntent) params.set("intent", "tutor");

    if (attributionData) {
      if (attributionData.utm_source) params.set("utm_source", attributionData.utm_source);
      if (attributionData.utm_medium) params.set("utm_medium", attributionData.utm_medium);
      if (attributionData.utm_campaign) params.set("utm_campaign", attributionData.utm_campaign);
      if (attributionData.utm_term) params.set("utm_term", attributionData.utm_term);
      if (attributionData.utm_content) params.set("utm_content", attributionData.utm_content);
      if (attributionData.gclid) params.set("gclid", attributionData.gclid);
      if (attributionData.gbraid) params.set("gbraid", attributionData.gbraid);
      if (attributionData.wbraid) params.set("wbraid", attributionData.wbraid);
      if (attributionData.dclid) params.set("dclid", attributionData.dclid);
      if (attributionData.gad_source) params.set("gad_source", attributionData.gad_source);
      if (attributionData.gad_campaignid)
        params.set("gad_campaignid", attributionData.gad_campaignid);
      if (attributionData.fbclid) params.set("fbclid", attributionData.fbclid);
      if (attributionData.network) params.set("network", attributionData.network);
      if (attributionData.ad_group) params.set("ad_group", attributionData.ad_group);
      if (attributionData.keywords) params.set("keywords", attributionData.keywords);
      if (attributionData.creative) params.set("creative", attributionData.creative);
    }

    if (sessionId) params.set("ph_session_id", sessionId);
    if (distinctId) params.set("ph_distinct_id", distinctId);

    if (typeof window !== "undefined") {
      stageOAuthPostHogBootstrap(window.sessionStorage, sessionId, distinctId);
    }

    if (params.toString()) {
      redirectTo += "?" + params.toString();
    }

    logger.info("Starting Google OAuth flow", {
      authMode,
      redirectTo,
      hasRedirectParam: !!redirectParam,
      isRunningAsPWA,
    });

    posthog?.capture("oauth_attempt", {
      provider: "google",
      auth_mode: authMode,
      has_attribution: !!attributionData?.utm_source,
      utm_source: attributionData?.utm_source,
      utm_medium: attributionData?.utm_medium,
      session_id: sessionId,
      distinct_id: distinctId,
      is_pwa: isRunningAsPWA,
    });

    if (redirectParam) {
      localStorage.setItem("auth_redirect", redirectParam);
    }

    await stageGuestVoiceSignup();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });

    if (error) {
      setMessage(error.message);
      posthog?.capture("oauth_error", {
        provider: "google",
        auth_mode: authMode,
        error: error.message,
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div
        className={
          isTutorIntent
            ? "mb-5 text-left"
            : `text-center ${variant === "modal" ? "mb-8 px-8" : "mb-8"}`
        }
      >
        {headingLevel === 2 ? (
          <h2 id={titleId} className="text-2xl font-bold text-white">
            {authTitle}
          </h2>
        ) : (
          <h1 id={titleId} className="text-2xl font-bold text-white">
            {authTitle}
          </h1>
        )}
        <p className="mt-2 text-[15px] leading-6 text-white/55">{authDescription}</p>
      </div>

      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={isLoading}
        className={secondaryButtonClassName}
      >
        <GoogleLogo />
        <span>Continue with Google</span>
      </button>

      <div className={`relative ${isTutorIntent ? "my-4" : "my-6"}`}>
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10" />
        </div>
        <div className="relative flex justify-center text-xs font-semibold uppercase tracking-wider">
          <span className="bg-[#121318] px-3 text-white/40">Or continue with email</span>
        </div>
      </div>

      <form onSubmit={handleAuthSubmit} className={isTutorIntent ? "space-y-4" : "space-y-6"}>
        <div
          className={
            isTutorIntent && authMode === "signup" ? "grid gap-4 sm:grid-cols-2" : "space-y-5"
          }
        >
          <div className={isTutorIntent && authMode === "signup" ? "sm:col-span-2" : ""}>
            <label htmlFor="email" className={`${labelClassName} mb-2`}>
              Email address
            </label>
            <div className="relative">
              {!isTutorIntent && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Mail className="h-4 w-4 text-white/40" aria-hidden="true" />
                </div>
              )}
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={`${inputClassName} ${isTutorIntent ? "px-3" : "pl-10 pr-3"}`}
                placeholder="you@example.com"
              />
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label htmlFor="password" className={labelClassName}>
                Password
              </label>
              {authMode === "signin" && (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isLoading}
                  className="text-xs font-semibold text-orange-300 transition-colors hover:text-orange-200"
                >
                  Forgot?
                </button>
              )}
            </div>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={`${inputClassName} px-3`}
              placeholder="••••••••"
            />
          </div>
          <AnimatePresence>
            {authMode === "signup" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <label htmlFor="confirmPassword" className={`${labelClassName} mb-2`}>
                  Confirm password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className={`${inputClassName} px-3`}
                  placeholder="••••••••"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <button type="submit" disabled={isLoading} className={primaryButtonClassName}>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {isLoading
            ? "Processing..."
            : authMode === "signup"
              ? isTutorIntent
                ? "Create tutor account"
                : "Create free account"
              : "Sign in"}
        </button>
      </form>

      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`mt-4 rounded-lg border p-3 text-center text-sm ${
              message.includes("successful") ||
              message.includes("verification") ||
              message.startsWith("If an account exists for this email")
                ? "border-green-500/20 bg-green-500/10 text-green-300"
                : "border-red-500/20 bg-red-500/10 text-red-300"
            }`}
          >
            {message}
          </motion.div>
        )}
      </AnimatePresence>

      <p className={`${isTutorIntent ? "mt-5" : "mt-8"} text-center text-sm text-white/55`}>
        {authMode === "signup" ? (
          <>
            Already have an account?{" "}
            <button
              type="button"
              onClick={toggleAuthMode}
              className="font-semibold text-orange-300 transition-colors hover:text-orange-200 focus:outline-none"
            >
              Sign in
            </button>
          </>
        ) : (
          <>
            Don&apos;t have an account?{" "}
            <button
              type="button"
              onClick={toggleAuthMode}
              className="font-semibold text-orange-300 transition-colors hover:text-orange-200 focus:outline-none"
            >
              Sign up
            </button>
          </>
        )}
      </p>
    </div>
  );
}
